import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { ActionsMenu } from '~/eds/src/components/ActionsMenu/ActionsMenu';
import { useToast } from '~/eds/src/components/hooks/useToast';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { IconButton } from '~/eds/src/components/IconButton/IconButton';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { LoadingSpinner } from '~/eds/src/components/LoadingSpinner/LoadingSpinner';
import { Text } from '~/eds/src/components/Text/Text';
import { Action } from '~/eds/src/types/Action';
import { useConversationPipeline } from '~/features/ask-anything/hooks/useConversationPipeline';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { api } from '~/redux/api/createApi';
import { slices } from '~/redux/slices';
import { QuestionGroup } from '~/types/QuestionGroup';
import { testIsAdmin } from '~/utils/user';

interface QuestionGroupItemProps {
  questionGroup: QuestionGroup;
  onQuestionGroupClick: (questionGroup: QuestionGroup) => void;
  disableRun?: boolean;
  status?: 'running' | 'complete' | 'idle';
  onAction?: ({
    action,
    questionGroup,
  }: {
    action: 'edit' | 'delete';
    questionGroup: QuestionGroup;
  }) => void;
}

export const QuestionGroupItem = ({
  questionGroup,
  disableRun,
  status,
  onAction,
  onQuestionGroupClick,
}: QuestionGroupItemProps) => {
  const intl = useIntl();
  const [isHovered, setIsHovered] = useState(false);
  const { toast } = useToast();
  const isRunning = status === 'running';
  const isComplete = status === 'complete';

  const [interruptAiMessage] = api.endpoints.interruptAiMessage.useMutation();
  const currentUser = useCurrentUser();

  const { queue, clearQueue } = useConversationPipeline();
  const conversationId = useSelector(
    slices.chatbot.selectors.selectCurrentConversationId,
  );
  const completedQuestions = queue.filter(
    (message) => message.status === 'complete',
  );

  const interruptAiMessageHandler = async () => {
    if (!conversationId) {
      return;
    }
    try {
      await interruptAiMessage(conversationId).unwrap();
      clearQueue();
    } catch {
      toast({
        status: 'danger',
        message: intl.formatMessage({
          id: 'QuestionGroupItem.stopFailedMessage',
          defaultMessage: 'Failed to stop Question Group',
          description: 'Error message when stopping question group fails',
        }),
      });
    }
  };

  /**
   * These are used to control the visibility of the "Run question group" button
   * Here are the rules:
   * 1. If it's not running, it should be visible when hovered
   * 2. If a qustion group is running, it should be visible if the running group is this group
   * 3. If a question group is running, it should be hidden if the running group is not this group
   * 4. If the run is complete, and this is the group that was run, it should show "Running complete" and a checkmark icon
   */
  const runText = isRunning
    ? intl.formatMessage(
        {
          id: 'QuestionGroupItem.runningText',
          defaultMessage: 'Running... {completed}/{total} complete',
          description:
            'Text showing running progress. Example: completed = 2, total = 5',
        },
        {
          completed: completedQuestions.length,
          total:
            queue.length > questionGroup.questionIds.length
              ? questionGroup.questionIds.length
              : queue.length,
        },
      )
    : intl.formatMessage({
        id: 'QuestionGroupItem.runText',
        defaultMessage: 'Run Question Group',
        description: 'Text for run question group button',
      });

  const shouldHideRun = disableRun && !isRunning;
  const isRunButtonVisible =
    (isHovered && !shouldHideRun) || isRunning || isComplete;

  const hasManagePermissions =
    questionGroup.createdBy === currentUser.id || testIsAdmin(currentUser);
  const getActions = useCallback(() => {
    const actions: Action[] = [
      {
        label: intl.formatMessage({
          id: 'QuestionGroupItem.editLabel',
          defaultMessage: 'Edit',
          description: 'Label for edit action',
        }),
        value: 'edit',
        onClick: () => onAction?.({ action: 'edit', questionGroup }),
      },
      {
        label: intl.formatMessage({
          id: 'QuestionGroupItem.deleteLabel',
          defaultMessage: 'Delete',
          description: 'Label for delete action',
        }),
        value: 'delete',
        onClick: () => onAction?.({ action: 'delete', questionGroup }),
      },
    ];

    return actions;
  }, [questionGroup, currentUser, onAction]);

  return (
    <Layout
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      data-eds-styles-disable-interactable
      align="flex-start"
      justify="space-between"
      flex={1}
    >
      <Layout
        flex={1}
        spacing={2}
        direction="column"
        align="flex-start"
        data-eds-styles-disable-interactable
        onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
          // this is necessary to override the default onClick behavior.
          // it should only expand/collapse the accordion when clicked in the chevron
          // clicking the summary would trigger the onQuestionGroupClick
          event.stopPropagation();
          event.preventDefault();
          !disableRun && onQuestionGroupClick(questionGroup);
        }}
      >
        <Text>{questionGroup.name}</Text>
        <Layout justify="space-between" w="100%">
          <Layout
            styles={{
              visibility: isRunButtonVisible ? 'visible' : 'hidden',
            }}
            spacing={2}
          >
            {isComplete ? (
              <Icon icon="check" color="status.success" />
            ) : isRunning ? (
              <LoadingSpinner mode="dashed" />
            ) : null}
            <Text size="s" color="action.link" variant="tiny">
              {isComplete
                ? intl.formatMessage({
                    id: 'QuestionGroupItem.runningCompleteText',
                    defaultMessage: 'Running Complete',
                    description:
                      'Text shown when question group run is complete',
                  })
                : runText}
            </Text>
          </Layout>
          {isRunning && (
            <IconButton
              PRIVATE_variant="action"
              size="xs"
              icon="circle-stop"
              tooltip={intl.formatMessage({
                id: 'QuestionGroupItem.stopTooltip',
                defaultMessage: 'Stop running Question Group',
                description: 'Tooltip for stop button',
              })}
              onClick={interruptAiMessageHandler}
            />
          )}
        </Layout>
      </Layout>
      <Layout align="start" justify="flex-end" minW={38} minH={40}>
        {isHovered && hasManagePermissions && (
          <ActionsMenu
            direction="right"
            name="question item actions"
            enablePortal={true}
            actions={getActions()}
          />
        )}
      </Layout>
    </Layout>
  );
};
